import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Car, Truck, ChevronRight, Clock, Calendar, Ban} from 'lucide-react'
import { computeVehicleAvailability } from '@/lib/availability'

// Définition des catégories avec icône et description
const CATEGORIES = [
  {
    key: 'Fourgon',
    label: 'Fourgon',
    description: 'Transport de marchandises et déménagement',
    icon: '🚐',
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    key: 'Plateau',
    label: 'Plateau',
    description: 'Transport de charges lourdes et matériaux',
    icon: '🛻',
    gradient: 'from-slate-600 to-slate-800',
  },

  {
    key: 'Citadine',
    label: 'Citadine',
    description: 'Idéale pour la ville et les courts trajets',
    icon: '🚗',
    gradient: 'from-emerald-500 to-emerald-700',
  },

  {
    key: 'SUV',
    label: 'SUV',
    description: 'Puissance et confort sur tous les terrains',
    icon: '🏎️',
    gradient: 'from-red-500 to-red-700',
  },
  {
    key: 'Berline',
    label: 'Berline',
    description: 'Élégance et confort pour vos déplacements',
    icon: '🚘',
    gradient: 'from-cyan-500 to-cyan-700',
  },
  {
    key: 'Premium',
    label: 'Premium',
    description: 'Le haut de gamme pour vos occasions spéciales',
    icon: '✨',
    gradient: 'from-yellow-500 to-amber-600',
  },
  {
    key: 'Monospace',
    label: 'Monospace',
    description: 'Confort familial et espace généreux',
    icon: '🚙',
    gradient: 'from-teal-500 to-teal-700',
  },
]

function formatDateFr(dateStr: string): string {
  const date = new Date(dateStr)
  const day = date.getDate()
  const months = ['jan.', 'fév.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
  const month = months[date.getMonth()]
  return `${day} ${month}`
}

interface CategoryVehicle {
  category: string
  vehicle: any | null
  availableNow: boolean
  nextAvailableDate?: string
  bookedUntil?: string
  vehicleCount: number
  isVehicleUnavailable: boolean // véhicule avec statut indisponible
}

export default function VehicleCategories() {
  const [categoryData, setCategoryData] = useState<CategoryVehicle[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const now = new Date().toISOString()

    Promise.all([
      // Charger TOUS les véhicules sauf cachés
      lumi.entities.vehicles.list({
        filter: { status: { $nin: ['cache'] } },
        limit: 100,
        sort: { pricePerDay: 1 },
      }),
      lumi.entities.vehicle_bookings.list({
        filter: {
          status: { $in: ['pending', 'confirmed', 'paid', 'active'] },
          endDate: { $gte: now },
        },
        sort: { endDate: 1 },
        limit: 200,
      }),
    ])
      .then(([vehiclesRes, reservationsRes]) => {
        const allVehicles = vehiclesRes?.list || []
        const activeReservations = reservationsRes?.list || []

        // Construire la map des réservations par véhicule
        const vehicleReservationMap = new Map<string, { startDate: string; endDate: string }[]>()
        activeReservations.forEach((r: any) => {
          const existing = vehicleReservationMap.get(r.vehicleId) || []
          existing.push({ startDate: r.startDate, endDate: r.endDate })
          vehicleReservationMap.set(r.vehicleId, existing)
        })

        const nowDate = new Date()

        // Pour CHAQUE catégorie définie, construire les données
        const results: CategoryVehicle[] = CATEGORIES.map((cat) => {
          const vehiclesInCategory = allVehicles.filter((v: any) => v.category === cat.key)

          if (vehiclesInCategory.length === 0) {
            return {
              category: cat.key,
              vehicle: null,
              availableNow: false,
              vehicleCount: 0,
              isVehicleUnavailable: false,
            }
          }

          // Séparer les véhicules réservables (available/disponible) des non-réservables
          const reservableVehicles = vehiclesInCategory.filter(
            (v: any) => v.status === 'available' || v.status === 'disponible'
          )
          const nonReservableVehicles = vehiclesInCategory.filter(
            (v: any) => v.status !== 'available' && v.status !== 'disponible'
          )

          // ÉTAPE 1 : chercher un véhicule réservable disponible MAINTENANT
          //   = status available/disponible ET aucune réservation en cours ET heure encore OK aujourd'hui
          let availableNowVehicle: any = null
          let availableNowBookedUntil: string | undefined
          const currentlyRentedCandidates: Array<{ v: any; nextDate: string }> = []

          for (const v of reservableVehicles) {
            const reservations = vehicleReservationMap.get(v._id) || []
            const { availableNow, nextAvailableDate, bookedUntil } = computeVehicleAvailability(reservations, nowDate)

            if (availableNow) {
              availableNowVehicle = v
              availableNowBookedUntil = bookedUntil
              break
            }

            if (nextAvailableDate) {
              currentlyRentedCandidates.push({ v, nextDate: nextAvailableDate })
            }
          }

          if (availableNowVehicle) {
            return {
              category: cat.key,
              vehicle: availableNowVehicle,
              availableNow: true,
              nextAvailableDate: undefined,
              bookedUntil: availableNowBookedUntil,
              vehicleCount: vehiclesInCategory.length,
              isVehicleUnavailable: false,
            }
          }

          // ÉTAPE 2 : tous les véhicules réservables sont en cours de location
          //   → prendre celui dont la prochaine date est la plus proche
          if (currentlyRentedCandidates.length > 0) {
            currentlyRentedCandidates.sort(
              (a, b) => new Date(a.nextDate).getTime() - new Date(b.nextDate).getTime()
            )
            const best = currentlyRentedCandidates[0]
            return {
              category: cat.key,
              vehicle: best.v,
              availableNow: false,
              nextAvailableDate: best.nextDate,
              vehicleCount: vehiclesInCategory.length,
              isVehicleUnavailable: false,
            }
          }

          // ÉTAPE 3 : aucun véhicule réservable → afficher un véhicule indisponible
          const fallback = nonReservableVehicles[0] || vehiclesInCategory[0]
          return {
            category: cat.key,
            vehicle: fallback,
            availableNow: false,
            nextAvailableDate: undefined,
            vehicleCount: vehiclesInCategory.length,
            isVehicleUnavailable: true,
          }
        })

        setCategoryData(results)
      })
      .catch((err) => console.error('Erreur chargement catégories:', err))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <section className="py-20 md:py-28 bg-gray-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#fbbf24] border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 mt-4 font-medium">Chargement des catégories...</p>
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 md:py-28 bg-gray-50 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-black to-transparent opacity-10" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#fbbf24]/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#fbbf24]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 bg-black text-[#fbbf24] px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-4">
            <Truck className="w-4 h-4" />
            Nos catégories
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-black leading-tight">
            Trouvez le véhicule
            <span className="block text-[#fbbf24]">qu'il vous faut</span>
          </h2>
          <p className="text-gray-500 text-lg mt-4 max-w-2xl mx-auto">
            Du fourgon au citadine, explorez toutes nos catégories de véhicules
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {categoryData.map(({ category, vehicle, availableNow, nextAvailableDate, bookedUntil, vehicleCount, isVehicleUnavailable }, index) => {
            const catConfig = CATEGORIES.find((c) => c.key === category)
            if (!catConfig) return null

            const hasVehicle = !!vehicle
            const isFullyUnavailable = !hasVehicle || (isVehicleUnavailable && !availableNow && !nextAvailableDate)

            return (
              <Link
                key={category}
                to={`/flotte?categorie=${encodeURIComponent(category)}`}
                className={`group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 hover:border-[#fbbf24] ${
                  isFullyUnavailable ? 'opacity-80' : ''
                }`}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                {/* Image véhicule ou gradient catégorie */}
                <div className="relative h-44 overflow-hidden">
                  {hasVehicle && vehicle.imageUrl ? (
                    <img
                      src={vehicle.imageUrl}
                      alt={`${vehicle.brand} ${vehicle.model}`}
                      className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${
                        isFullyUnavailable ? 'grayscale-[30%]' : ''
                      }`}
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${catConfig.gradient} flex items-center justify-center`}>
                      <span className="text-7xl opacity-80">{catConfig.icon}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                  {/* Catégorie badge */}
                  <div className="absolute top-3 left-3">
                    <span className="inline-flex items-center gap-1.5 bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold">
                      <span>{catConfig.icon}</span>
                      {catConfig.label}
                    </span>
                  </div>

                  {/* Nombre de véhicules */}
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-black px-2.5 py-1 rounded-full text-xs font-bold">
                      <Car className="w-3 h-3" />
                      {vehicleCount}
                    </span>
                  </div>

                  {/* Nom du véhicule ou message */}
                  <div className="absolute bottom-3 left-3 right-3">
                    {hasVehicle ? (
                      <>
                        <p className="text-white font-black text-lg drop-shadow-lg truncate">
                          {vehicle.brand} {vehicle.model}
                        </p>
                        <p className="text-[#fbbf24] text-sm font-semibold">{vehicle.name}</p>
                      </>
                    ) : (
                      <p className="text-white/80 font-bold text-sm drop-shadow-lg">
                        Bientôt disponible
                      </p>
                    )}
                  </div>
                </div>

                {/* Contenu */}
                <div className="p-5">
                  {/* Description catégorie */}
                  <p className="text-gray-500 text-sm mb-4 line-clamp-2">{catConfig.description}</p>

                  {/* Disponibilité */}
                  {availableNow ? (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-xl mb-4">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse flex-shrink-0" />
                      <p className="text-xs font-bold">Disponible immédiatement</p>
                    </div>
                  ) : nextAvailableDate ? (
                    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 text-orange-800 px-3 py-2 rounded-xl mb-4">
                      <Calendar className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                      <p className="text-xs font-bold">
                        Dès le <span className="font-black">{formatDateFr(nextAvailableDate)}</span>
                      </p>
                    </div>
                  ) : isVehicleUnavailable || !hasVehicle ? (
                    <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 text-gray-600 px-3 py-2 rounded-xl mb-4">
                      <Ban className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <p className="text-xs font-bold">Indisponible actuellement</p>
                    </div>
                  ) : null}

                  {/* Prix + CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      {hasVehicle && vehicle.pricePerDay ? (
                        vehicle.pricePerHalfDay && vehicle.pricePerHalfDay > 0 ? (
                          <div className="flex items-end gap-2.5">
                            <div>
                              <p className="text-xs font-bold text-gray-400 mb-0.5">½ J</p>
                              <p className="text-lg font-black text-black leading-none">
                                CHF {vehicle.pricePerHalfDay}
                              </p>
                            </div>
                            <div className="w-px h-8 bg-gray-200" />
                            <div>
                              <p className="text-xs font-bold text-gray-400 mb-0.5">J</p>
                              <p className="text-lg font-black text-black leading-none">
                                CHF {vehicle.pricePerDay}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-2xl font-black text-black">
                              CHF {vehicle.pricePerDay}
                            </p>
                            <p className="text-gray-400 text-xs font-medium">dès / jour</p>
                          </>
                        )
                      ) : (
                        <>
                          <p className="text-lg font-black text-gray-400">—</p>
                          <p className="text-gray-400 text-xs font-medium">Tarif sur demande</p>
                        </>
                      )}
                    </div>
                    <span
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition-all group-hover:scale-105 ${
                        availableNow
                          ? 'bg-[#fbbf24] text-black group-hover:bg-[#f59e0b]'
                          : 'bg-black text-[#fbbf24] group-hover:bg-black/90'
                      }`}
                    >
                      Voir
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}
